'use client';

// Bottom navigation dla mobile (Z Fold 6 cover / inner portrait).
// Aktywne <768px (sidebar znika). 5 ikon — najczęściej używane workflow.
// Profile menu otwiera się jako bottom-sheet (osobne).

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, Building2, Plus, Inbox, MoreHorizontal,
  ScanLine, Users, BarChart3, Settings, LogOut, X, Target, Upload, Package,
} from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { signOutAction } from '@/lib/actions/auth';

type BottomNavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  badge?: number;
};

function getPrimaryNav(inboxCount: number): BottomNavItem[] {
  return [
    { href: '/', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/dostawcy', label: 'Dostawcy', icon: Building2 },
    { href: '/dodaj', label: 'Dodaj', icon: Plus },
    { href: '/inbox', label: 'Inbox', icon: Inbox, badge: inboxCount > 0 ? inboxCount : undefined },
  ];
}

const MORE_NAV: BottomNavItem[] = [
  { href: '/produkty', label: 'Produkty · SKU', icon: Package },
  { href: '/akcje', label: 'Akcje', icon: Target },
  { href: '/import', label: 'Import TSV', icon: Upload },
  { href: '/skanuj', label: 'Skanuj wizytówkę', icon: ScanLine },
  { href: '/kontakty', label: 'Kontakty', icon: Users },
  { href: '/raporty', label: 'Raporty', icon: BarChart3 },
  { href: '/ustawienia', label: 'Ustawienia', icon: Settings },
];

export type BottomNavUser = {
  name: string | null;
  email: string;
  image: string | null;
};

export function BottomNav({
  user,
  inboxCount = 0,
}: {
  user: BottomNavUser | null;
  inboxCount?: number;
}) {
  const pathname = usePathname();
  const [moreOpen, setMoreOpen] = useState(false);
  const primaryNav = getPrimaryNav(inboxCount);

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  return (
    <>
      <nav className="bottom-nav" aria-label="Główna nawigacja">
        {primaryNav.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
              style={{ position: 'relative' }}
            >
              <Icon size={20} strokeWidth={active ? 2.2 : 1.75} />
              <span className="label">{item.label}</span>
              {item.badge && item.badge > 0 && (
                <span className="bottom-nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
            </Link>
          );
        })}
        <button
          type="button"
          className={`bottom-nav-item ${moreOpen ? 'active' : ''}`}
          onClick={() => setMoreOpen(true)}
          aria-label="Więcej opcji + profil"
        >
          {user ? (
            <Avatar
              name={user.name ?? user.email.split('@')[0]}
              imageUrl={user.image}
              size="sm"
            />
          ) : (
            <MoreHorizontal size={20} />
          )}
          <span className="label">Więcej</span>
        </button>
      </nav>

      {moreOpen && (
        <MoreSheet user={user} onClose={() => setMoreOpen(false)} pathname={pathname} />
      )}
    </>
  );
}

// =============================================================================
// MoreSheet — bottom-sheet z dodatkowymi linkami + user info + logout
// =============================================================================

function MoreSheet({
  user,
  onClose,
  pathname,
}: {
  user: BottomNavUser | null;
  onClose: () => void;
  pathname: string;
}) {
  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  return (
    <div className="bottom-sheet-backdrop" onClick={onClose}>
      <div
        className="bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Więcej opcji"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-head">
          <h3 className="t-h3" style={{ margin: 0 }}>Więcej</h3>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            aria-label="Zamknij"
          >
            <X size={14} />
          </button>
        </div>

        {user && (
          <div className="bottom-sheet-user">
            <Avatar
              name={user.name ?? user.email.split('@')[0]}
              imageUrl={user.image}
              size="md"
            />
            <div className="col" style={{ gap: 1, minWidth: 0 }}>
              <div className="t-strong" style={{ fontSize: 14 }}>
                {user.name ?? user.email.split('@')[0]}
              </div>
              <div className="t-xs t-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email}
              </div>
            </div>
          </div>
        )}

        <nav className="bottom-sheet-nav">
          {MORE_NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`bottom-sheet-nav-item ${active ? 'active' : ''}`}
                onClick={onClose}
              >
                <Icon size={18} strokeWidth={1.75} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <form action={signOutAction} style={{ margin: 0 }}>
            <button
              type="submit"
              className="bottom-sheet-nav-item bottom-sheet-nav-item-danger"
            >
              <LogOut size={18} strokeWidth={1.75} />
              <span>Wyloguj się</span>
            </button>
          </form>
        </nav>
      </div>
    </div>
  );
}
